import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordService } from '@core/services/password.service';
import { ApiResponse } from '@src/app/core/models/api-response.model';
import { ChangePasswordResponse } from '@src/app/core/models/change-password-response.model';
import { ElementControlService } from '@src/app/core/services/element-control.service';
import AES from 'crypto-js/aes';
import encUtf8 from 'crypto-js/enc-utf8';
import { RecaptchaComponent } from 'ng-recaptcha';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BaseComponent, UserProfileService } from 'sigv-library';
import { isNullOrEmpty } from 'sigv-library/util';
import { BackgroundImageService } from '../../services/background-image.service';

interface ResetPasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  recaptchaToken?: string;
}

@Component({
  selector: 'app-forgot-password-form',
  templateUrl: './forgot-password-form.component.html',
  styleUrls: ['./forgot-password-form.component.scss'],
})
export class ForgotPasswordFormComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('resetPasswordForm', { read: NgForm }) resetPasswordForm: any;
  @ViewChild(RecaptchaComponent) recaptcha?: RecaptchaComponent;
  @ViewChild('confirmPasswordDom', {read: NgModel}) confirmPasswordDom?: NgModel;

  blobUrlLogin = '';
  blobUrlBg = '';
  isChangeMode = false;
  resetPasswordFormModel: ResetPasswordForm;
  isPasswordRequired = false;
  checkAccount = '';
  checkDetail = '';
  isAtLogin = false;
  //是否顯示google驗證圖形
  elementControlJSON={"Login": {
    "GoogleReCaptcha": {
      "display": false
    }
  }};
  private readonly destroy$ = new Subject();
  private resizeSubscription: Subscription;


  constructor(
    protected injector: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private passwordService: PasswordService,
    private userProfileService: UserProfileService,
    private elementControlService:ElementControlService,
    private backgroundImageService:BackgroundImageService,
  ) {
    super(injector);
    this.resetPasswordFormModel = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    this.resizeSubscription = this.backgroundImageService.resize$.subscribe(() => {
      this.blobUrlBg = this.backgroundImageService.blobUrlBg;
      this.blobUrlLogin = this.backgroundImageService.blobUrlLogin;
    });
    
  }

  ngOnInit(): void {
    //取得是否顯示google驗證圖形
    this.elementControlService.getElementControl()
    .pipe(takeUntil(this.destroy$))
    .subscribe((elementControlJSON)=>{
      if(isNullOrEmpty(elementControlJSON)===false){
         this.elementControlJSON = elementControlJSON;
      }
    });
    this.isChangeMode =
      this.route.snapshot.routeConfig?.path === 'changePassword';
    if (!this.isChangeMode) {
      this.isAtLogin = true;
      this.route.queryParams.subscribe((params) => {
        const { account, s } = params;
        if (s) {
          this.passwordService
            .checkIdentity({
              account: account ? account : '',
              checkDetail: s,
            })
            .subscribe((isSuccess) => {
              if (isSuccess) {
                this.checkAccount = account;
                this.checkDetail = s;
              } else {
                this.router.navigate([
                  '/login'
                ], {
                  queryParams: { state: 'invalidatingForgotPsw' }
                });
              }
            });
        } else {
          this.router.navigate(['/login'], {
            queryParams: { state: 'invalidatingForgotPsw' }
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeSubscription.unsubscribe();
  }

  newPasswordChange(): void {
    setTimeout(() => {
      this.confirmPasswordDom?.control.updateValueAndValidity();
    });
  }

  submit(): void {
    combineLatest([
      this.passwordService.getSystemParameter('AAD.DesKey'),
      this.passwordService.getSystemParameter('AAD.DesIV'),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          return response.every(
            ({ data, rtnCode }) => data?.length && rtnCode === 0
          )
            ? (response
                .map(({ data }) => data?.[0]?.value)
                .filter((value) => !!value) as string[])
            : null;
        })
      )
      .subscribe((response) => {
        if (response) {
          this.changePassword(response);
        }
      });
  }

  changePassword([desKey, desIV]: string[]): void {
    const { currentPassword, newPassword, confirmPassword, recaptchaToken } =
      this.resetPasswordFormModel;
    const currentUserValue = this.userProfileService.currentUserValue;
    const account = currentUserValue?.userNo || currentUserValue?.email || '';
    const isChangeMode = this.isChangeMode;
    const isPass = isChangeMode
      ? currentPassword && newPassword && confirmPassword
      : newPassword && confirmPassword;
    const key = encUtf8.parse(desKey);
    const iv = encUtf8.parse(desIV);
    const currentPasswordEncode = currentPassword
      ? AES.encrypt(currentPassword, key ?? '', { iv }).toString()
      : '';
    const newPasswordEncode = newPassword
      ? AES.encrypt(newPassword, key ?? '', { iv }).toString()
      : '';

    const isRecaptchaToken = (this.elementControlJSON.Login.GoogleReCaptcha.display)? recaptchaToken : true;

    if (isPass && isRecaptchaToken) {
      let changePassword$: Observable<ApiResponse<ChangePasswordResponse> | null>;
      // 變更密碼
      if (isChangeMode) {
        changePassword$ = this.passwordService.changePassword({
          account,
          currentPassword: currentPasswordEncode,
          newPassword: newPasswordEncode,
          recaptchaToken:recaptchaToken ?? '',
        });
      } else {
        // 忘記密碼
        changePassword$ = this.passwordService.changeForgotPassword({
          account: this.checkAccount,
          newPassword: newPasswordEncode,
          checkDetail: this.checkDetail,
          recaptchaToken:recaptchaToken ?? '',
        });
      }

      changePassword$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
        if (response && response.data?.isSuccessReset) {
          this.translateService
            .get('ForgotPassword.ChangePasswordSuccess')
            .subscribe({
              next: (str) => {
                this.toastService.success(str);
                if (isChangeMode) {
                  setTimeout(() => {
                    this.router.navigate(['/']);
                  }, 800);
                } else {
                  // 忘記密碼變更回到登入頁面
                  setTimeout(() => {
                    localStorage.setItem(
                      'forgotPasswordAccount',
                      response?.data?.account ?? ''
                    );
                    this.router.navigate(['/login']);
                  }, 800);
                }
              },
            });
        } else {
          this.recaptcha?.reset();
          this.dialogService.showErrorDialog(
            [
              {
                description: this.translateService.instant(
                  'ForgotPassword.ChangePasswordFailDescription'
                ),
              },
            ],
            this.translateService.instant('ForgotPassword.ChangePasswordFail')
          );
        }
      });
    }
  }

  clear(): void {
    this.resetPasswordFormModel = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };
  }

  errored(event: any): void {
    console.warn(`reCAPTCHA error encountered \n `, event);
  }
}
